// IR-1757 r1 (Rotem 66093): the Avatars tab shows every per-environment value
// twice, once labelled DEV and once labelled Production, and every value that
// truly applies to both exactly once, labelled "Shared across DEV and
// Production". The pair follows the Monitoring tab and IR-1697's Ingestion
// pair: one block per environment, never one merged number.
//
// A side that could not be read says "Could Not Check" -- never 0. When the
// backend response carries no `env_values` (iris-ops ahead of iris-backend),
// the cell renders exactly what it did before: no labels, no invented DEV
// reading.
import type { AvatarEnvSide, AvatarEnvValues } from "@/lib/types";
import type { ReactNode } from "react";

export const SHARED_LABEL = "Shared across Development and Production";
// One wording, the Monitoring tab's: Production / Development (r2 MINOR).
export const ENV_LABEL = { prod: "Production", dev: "Development" } as const;
export const COULD_NOT_CHECK_WORD = "Could Not Check";

export type EnvField = "groups" | "delivering" | "archived" | "capacity_held";

/** What one side prints for one field: the number, or Could Not Check, or an em dash. */
export function envSideText(side: AvatarEnvSide | null | undefined, field: EnvField): string {
  if (!side) return "—";
  if (side.could_not_check) return COULD_NOT_CHECK_WORD;
  // r2: only Delivering is unreadable (stale Production rollup / DEV rollup).
  if (field === "delivering" && side.delivering_could_not_check) return COULD_NOT_CHECK_WORD;
  // r3 (B1): an unreadable seat count is Could Not Check, never 0 or a dash.
  if (field === "capacity_held" && side.capacity_could_not_check) return COULD_NOT_CHECK_WORD;
  const v = side[field];
  return v === null || v === undefined ? "—" : String(v);
}

export function SharedLabel({ testId }: { testId?: string }) {
  return (
    <span
      data-testid={testId ?? "shared-across-envs-label"}
      style={{ fontSize: 9, fontWeight: 400, color: "var(--iris-fg-4)", whiteSpace: "nowrap" }}
    >
      {SHARED_LABEL}
    </span>
  );
}

function EnvTag({ env }: { env: "prod" | "dev" }) {
  return (
    <span
      data-testid={`env-label-${env}`}
      style={{ fontSize: 9, color: "var(--iris-fg-4)", whiteSpace: "nowrap" }}
    >
      {ENV_LABEL[env]}
    </span>
  );
}

/**
 * One per-environment cell: the Production reading (the legacy content, passed
 * as `children`, untouched) with its label, then the DEV reading with its own.
 * Without `env_values` the legacy content renders alone.
 */
export function EnvPairCell({
  env,
  field,
  testId,
  children,
  prodFromEnv = false,
  prodTitle,
}: {
  env: AvatarEnvValues | null | undefined;
  field: EnvField;
  testId: string;
  children: ReactNode;
  /**
   * r2 (F1): render the Production half from `env.prod[field]` instead of the
   * legacy `children`. Used where the legacy content is NOT the Production
   * value (Groups prints the phone's confirmed count; Archived prints the
   * phone's archived folder beside IRIS's). Delivering keeps its children: the
   * legacy figure IS the Production rollup value there.
   */
  prodFromEnv?: boolean;
  prodTitle?: string;
}) {
  if (!env) return <>{children}</>;
  const prodUnreadable = env.prod?.could_not_check === true;
  const devText = envSideText(env.dev, field);
  return (
    <div data-testid={testId} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <div
        data-testid={`${testId}-prod`}
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "baseline",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        <EnvTag env="prod" />
        <div style={{ textAlign: "right" }}>
          {prodFromEnv ? (
            <span title={prodTitle}>{envSideText(env.prod, field)}</span>
          ) : prodUnreadable ? (
            <span>{COULD_NOT_CHECK_WORD}</span>
          ) : (
            children
          )}
        </div>
      </div>
      <div
        data-testid={`${testId}-dev`}
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "baseline",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        <EnvTag env="dev" />
        <span>{devText}</span>
      </div>
    </div>
  );
}

/** Seats held, per environment, as one caption line under the capacity readout. */
export function CapacityEnvCaption({
  env,
  stopped = false,
}: {
  env: AvatarEnvValues | null | undefined;
  stopped?: boolean;
}) {
  if (!env) return null;
  return (
    <div
      className="iris-td--muted"
      style={{ fontSize: 10, whiteSpace: "nowrap" }}
      data-testid="capacity-env-caption"
    >
      {stopped ? "Seats held, not collecting" : "Seats held"}: Production{" "}
      {envSideText(env.prod, "capacity_held")} · Development {envSideText(env.dev, "capacity_held")}
    </div>
  );
}
